/**
 * Idle intents: the wire form of an idle world action (D240 to D246), validated the way the
 * old game's intents are (`intents.ts`): every argument is checked here before it reaches
 * the pure action functions in `idle/world.ts`, and this table is the one place that maps a
 * name to a function.
 *
 * Adding an action is one line in `IDLE_ACTIONS`: its arguments as a zod shape and the call.
 * The union, the envelope and the dispatcher are all built from the table.
 */
import { z } from 'zod';
import type { IdleContent } from '@hedgerow/data';
import {
  buildIdleChain,
  buyIdle,
  buyIdleMastery,
  buyIdleUpgrade,
  hireIdleHand,
  tapIdle,
  unlockIdleProducer,
  upgradeIdleBarn,
  type IdleResult,
} from './idle/world.ts';
import { claimIdleBonus } from './idle/bonuses.ts';
import {
  buyIdlePerk,
  buyIdleTrait,
  holdIdleFestival,
  passOnIdleWorld,
  startIdleYear,
} from './idle/festival.ts';
import { buyIdleDecoration, setIdleDecoration } from './idle/decorations.ts';
import { sendIdleOrder } from './idle/daily.ts';
import { moveIdlePlot } from './idle/arrange.ts';
import type { IdleWorldState } from './idle/state.ts';

const id = z.string().min(1).max(64);
/** A map tile's coordinate, for a plot moved to free ground. */
const tile = z.number().int().min(-64).max(256);
/** How many of a producer one `buy` may take. The sim refuses what the world cannot pay for. */
const count = z.number().int().positive().max(10_000);

/** An action's arguments. Mapped over the shape, so an empty shape is `{}`, not `Record<string, never>`. */
type Args<S extends z.ZodRawShape> = { [P in keyof S]: z.infer<S[P]> };

type Run<S extends z.ZodRawShape> = (
  state: IdleWorldState,
  content: IdleContent,
  args: Args<S>,
  now: number,
) => IdleResult;

const act = <S extends z.ZodRawShape>(shape: S, run: Run<S>) => ({ shape, run });

/** Every idle action, by intent type. One line each. */
export const IDLE_ACTIONS = {
  tap: act({ producer: id }, (s, c, a, now) => tapIdle(s, c, a.producer, now)),
  buy: act({ producer: id, n: count }, (s, c, a, now) => buyIdle(s, c, a.producer, a.n, now)),
  hireHand: act({ producer: id }, (s, c, a, now) => hireIdleHand(s, c, a.producer, now)),
  unlock: act({ producer: id }, (s, c, a, now) => unlockIdleProducer(s, c, a.producer, now)),
  upgradeBarn: act({}, (s, c, _a, now) => upgradeIdleBarn(s, c, now)),
  buildChain: act({ chain: id }, (s, c, a, now) => buildIdleChain(s, c, a.chain, now)),
  buyUpgrade: act({ upgrade: id }, (s, c, a, now) => buyIdleUpgrade(s, c, a.upgrade, now)),
  /** D457: the next level of an Almanac branch's mastery step. */
  buyMastery: act({ branch: id }, (s, c, a, now) => buyIdleMastery(s, c, a.branch, now)),
  holdFestival: act({}, (s, c, _a, now) => holdIdleFestival(s, c, now)),
  startYear: act({}, (s, c, _a, now) => startIdleYear(s, c, now)),
  buyPerk: act({ perk: id }, (s, c, a, now) => buyIdlePerk(s, c, a.perk, now)),
  passOn: act({}, (s, c, _a, now) => passOnIdleWorld(s, c, now)),
  buyTrait: act({ trait: id }, (s, c, a, now) => buyIdleTrait(s, c, a.trait, now)),
  claimBonus: act({ offer: z.string().min(1).max(64) }, (s, c, a, now) =>
    claimIdleBonus(s, c, a.offer, now),
  ),
  buyDecoration: act({ decoration: id }, (s, c, a, now) =>
    buyIdleDecoration(s, c, a.decoration, now),
  ),
  setDecoration: act({ decoration: id }, (s, c, a, now) =>
    setIdleDecoration(s, c, a.decoration, now),
  ),
  /** D304: send one of the day's orders, by its place in the list. */
  sendOrder: act({ order: z.number().int().min(0).max(9) }, (s, c, a, now) =>
    sendIdleOrder(s, c, a.order, now),
  ),
  /** D454 step 3: drop a plot on another plot, and the two swap places. Free. */
  swapPlots: act({ producer: id, with: id }, (s, c, a) =>
    moveIdlePlot(s, c, a.producer, { with: a.with }),
  ),
  /** D454 step 3: move a plot to free ground, its top-left tile at x,y. Free. */
  movePlot: act({ producer: id, x: tile, y: tile }, (s, c, a) =>
    moveIdlePlot(s, c, a.producer, { x: a.x, y: a.y }),
  ),
};

type Actions = typeof IDLE_ACTIONS;
export type IdleIntentType = keyof Actions;

/** One idle intent: `{ type, ...args }`, for example `{ type: 'buy', producer: 'wheat', n: 1 }`. */
export type IdleIntent = {
  [K in IdleIntentType]: { type: K } & Args<Actions[K]['shape']>;
}[IdleIntentType];

const members = Object.entries(IDLE_ACTIONS).map(([type, a]) =>
  z.object({ type: z.literal(type), ...a.shape }).strict(),
);

export const IdleIntentSchema = z.discriminatedUnion(
  'type',
  members as unknown as [(typeof members)[number], ...(typeof members)[number][]],
);

/** `POST /farm/act` on an idle world. `id` is the idempotency key, as for the old envelope. */
export const IdleIntentEnvelope = z
  .object({
    id: z.string().uuid(),
    clientTime: z.number().int(),
    intents: z.array(IdleIntentSchema).min(1).max(20),
  })
  .strict();

export interface IdleIntentEnvelope {
  id: string;
  clientTime: number;
  intents: IdleIntent[];
}

/** Apply one validated intent. */
export function applyIdleIntent(
  state: IdleWorldState,
  content: IdleContent,
  intent: IdleIntent,
  now: number,
): IdleResult {
  const a = IDLE_ACTIONS[intent.type] as {
    run: (s: IdleWorldState, c: IdleContent, args: IdleIntent, now: number) => IdleResult;
  };
  return a.run(state, content, intent, now);
}
